import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_ORIGIN = (import.meta.env.VITE_API_BASE ?? "/api")
  .replace(/\/$/, "").replace(/\/api$/, "");
import { api } from "../api";

// ─── Utilities ───────────────────────────────────────────────────────────────

function fmtDate(utcStr: string): string {
  if (!utcStr) return "—";
  const d = new Date(utcStr.replace(" ", "T") + "Z");
  return d.toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued:    "bg-zinc-100 text-zinc-600",
    running:   "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    failed:    "bg-red-100 text-red-700",
    cancelled: "bg-orange-100 text-orange-700",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${map[status] ?? "bg-zinc-100 text-zinc-600"}`}>
      {status}
    </span>
  );
}

const OP_LABEL: Record<string, string> = {
  translate: "Dil çoklama",
  resize: "Boyut alma",
  video: "Videolaştırma",
  "image-edit": "Image to Image",
};

// ─── Types ────────────────────────────────────────────────────────────────────

type GalleryTab = "all" | "image" | "scenario" | "avatar" | "edit" | "compose" | "kampanya" | "long_story" | "vox_ad" | "broll_ad";

const TABS: { value: GalleryTab; label: string }[] = [
  { value: "all",        label: "Tümü" },
  { value: "image",      label: "Image" },
  { value: "scenario",   label: "Scenario" },
  { value: "avatar",     label: "Avatar" },
  { value: "long_story", label: "Long Story" },
  { value: "vox_ad",     label: "Vox Ad" },
  { value: "broll_ad",   label: "B-Roll Ad" },
  { value: "edit",       label: "Edit" },
  { value: "compose",    label: "Compose" },
  { value: "kampanya",   label: "Bulk Üretim" },
];

// ─── Root ────────────────────────────────────────────────────────────────────

export function Gallery({ productId }: { productId: string }) {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as GalleryTab) ?? "image";
  const [tab, setTab] = useState<GalleryTab>(initialTab);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sub-tabs */}
      <div className="flex border-b bg-white px-6 shrink-0">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`px-4 py-3 text-sm font-medium border-b-2 mr-1 transition-colors ${
              tab === t.value
                ? "border-accent text-accent"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">
        {tab === "all"      && <AllTab      productId={productId} />}
        {tab === "image"    && <ImageTab    productId={productId} />}
        {tab === "scenario" && <ScenarioTab productId={productId} />}
        {tab === "avatar"   && <AvatarTab   productId={productId} />}
        {tab === "long_story" && <LongStoryTab productId={productId} />}
        {tab === "vox_ad"     && <VoxAdTab     productId={productId} />}
        {tab === "broll_ad"   && <BrollAdTab   productId={productId} />}
        {tab === "edit"     && <EditTab     productId={productId} />}
        {tab === "compose"    && <ComposeTab    productId={productId} />}
        {tab === "kampanya"   && <KampanyaTab   productId={productId} />}
      </div>
    </div>
  );
}

// ─── IMAGE TAB ───────────────────────────────────────────────────────────────

interface MediaFile {
  id: string;
  media_id: string;
  language: string;
  size: string;
  drive_file_id: string;
  file_name: string;
}

interface MediaRow {
  id: string;
  code: string;
  type: string;
  angle: string;
  stage: string;
  theme: string;
  hook: string;
  body: string;
  cta: string;
  feature_name: string;
  job_created_at: string;
  drive_folder: string;
  image_prompt?: string;
  files: MediaFile[];
}

interface InspectRow {
  code: string;
  type: string;
  angle: string;
  stage: string;
  theme: string;
  hook: string;
  body: string;
  cta: string;
  image_prompt?: string;
}

function DriveFolderLink({ folderId, fileCount }: { folderId: string; fileCount: number }) {
  if (!folderId) return <span className="text-zinc-300">—</span>;
  return (
    <a
      href={`https://drive.google.com/drive/folders/${folderId}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 whitespace-nowrap"
    >
      📁 {fileCount > 0 ? `${fileCount} dosya` : "Klasör"} ↗
    </a>
  );
}

function PromptModal({ row, onClose }: { row: InspectRow; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <span className="font-mono text-xs text-zinc-400">{row.code}</span>
            <div className="text-sm font-semibold text-zinc-800 mt-0.5">
              {row.type} · {row.angle}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
              row.stage === "Unaware" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
            }`}>{row.stage}</span>
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
              row.theme === "dark" ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-600"
            }`}>{row.theme}</span>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 ml-2 text-lg leading-none">✕</button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {[
            { label: "Hook", value: row.hook, color: "text-violet-700 bg-violet-50 border-violet-200" },
            { label: "Body", value: row.body, color: "text-zinc-700 bg-zinc-50 border-zinc-200" },
            { label: "CTA",  value: row.cta,  color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1">{label}</div>
              <div className={`text-sm rounded-lg border px-3 py-2 ${color}`}>{value || "—"}</div>
            </div>
          ))}
          <div>
            <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1">Image Prompt (fal.ai)</div>
            {row.image_prompt ? (
              <div className="text-xs rounded-lg border px-3 py-2 bg-zinc-900 text-zinc-200 border-zinc-700 leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap font-mono">
                {row.image_prompt}
              </div>
            ) : (
              <div className="text-xs rounded-lg border px-3 py-2 bg-zinc-50 text-zinc-400 border-zinc-200 italic">
                Bu görsel yeni Drive yapısından önce üretildi — prompt kaydedilmemiş.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageTab({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [activeJobs, setActiveJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTheme, setFilterTheme] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [inspectRow, setInspectRow] = useState<InspectRow | null>(null);
  const [lightbox, setLightbox] = useState<{ files: MediaFile[]; index: number; code: string } | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const stopRef = useRef(false);

  function makeAvatarState(row: MediaRow) {
    return {
      fromGallery: true,
      imageCode:  row.code,
      painPoint:  row.angle    ?? "",
      hook:       row.hook     ?? "",
      body:       row.body     ?? "",
      cta:        row.cta      ?? "",
      featureName:   row.feature_name ?? "",
    };
  }

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const [mediaRes, jobsRes] = await Promise.all([
        api.listMedia(productId),
        api.listJobs(productId),
      ]);
      if (stopRef.current) return;
      setRows([...(mediaRes.media as MediaRow[])].sort((a, b) =>
        b.job_created_at.localeCompare(a.job_created_at)
      ));
      setActiveJobs((jobsRes.jobs ?? []).filter((j: any) =>
        j.status === "queued" || j.status === "running",
      ));
      setLoading(false);
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function cancelJob(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      await api.cancelJob(jobId);
      setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (e) {
      alert("İptal edilemedi: " + (e as Error).message);
    } finally {
      setCancelling(null);
    }
  }

  const types = useMemo(() => Array.from(new Set(rows.map((r) => r.type))).filter(Boolean).sort(), [rows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => {
      if (filterTheme !== "all" && r.theme !== filterTheme) return false;
      if (filterType !== "all" && r.type !== filterType) return false;
      if (q) return (
        r.hook?.toLowerCase().includes(q) ||
        r.angle?.toLowerCase().includes(q) ||
        r.cta?.toLowerCase().includes(q) ||
        r.type?.toLowerCase().includes(q) ||
        r.feature_name?.toLowerCase().includes(q) ||
        r.code?.toLowerCase().includes(q)
      );
      return true;
    });
  }, [rows, search, filterTheme, filterType]);

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 gap-3">
      {/* Active jobs banner */}
      {activeJobs.length > 0 && (
        <div className="shrink-0 border rounded-lg bg-blue-50 border-blue-200 overflow-hidden max-h-64">
          <div className="px-4 py-2 border-b border-blue-200 text-xs font-semibold text-blue-700">
            Aktif üretimler ({activeJobs.length})
          </div>
          <div className="overflow-y-auto max-h-48">
          <table className="w-full text-xs">
            <tbody>
              {activeJobs.map((j) => (
                <tr key={j.id} className="border-b border-blue-100 last:border-0">
                  <td className="px-4 py-2 font-mono text-zinc-500">{j.id.slice(0, 8)}</td>
                  <td className="px-4 py-2"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-2 text-zinc-500">{fmtDate(j.created_at)}</td>
                  <td className="px-4 py-2 flex gap-3">
                    {j.status === "queued" && (
                      <button
                        onClick={async () => { await api.requeueJob(j.id); }}
                        className="text-blue-600 hover:underline"
                      >tekrar tetikle</button>
                    )}
                    <button
                      disabled={cancelling === j.id}
                      onClick={() => cancelJob(j.id)}
                      className="text-red-500 hover:underline disabled:opacity-40"
                    >
                      {cancelling === j.id ? "durduruluyor…" : "durdur"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="shrink-0 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-52"
        />
        <select value={filterTheme} onChange={(e) => setFilterTheme(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
          <option value="all">Theme: hepsi</option>
          <option value="light">light</option>
          <option value="dark">dark</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
          <option value="all">Type: hepsi</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <span className="text-xs text-zinc-400 ml-auto">{filtered.length} kayıt</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-zinc-500">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500">Kayıt bulunamadı.</div>
      ) : (
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr>
                {[
                  { label: "Önizleme", cls: "w-16" },
                  { label: "Tarih",  cls: "w-32" },
                  { label: "Kod",    cls: "w-20" },
                  { label: "Type",   cls: "w-28" },
                  { label: "Angle",  cls: "" },
                  { label: "Stage",  cls: "w-24" },
                  { label: "Theme",  cls: "w-20" },
                  { label: "Feature", cls: "w-24" },
                  { label: "Prompt", cls: "w-16 text-center" },
                  { label: "Drive",  cls: "w-32" },
                  { label: "",       cls: "w-20" },
                ].map((col) => (
                  <th key={col.label} className={`text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap ${col.cls}`}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                  <td className="px-2 py-1.5 border-b border-zinc-100">
                    {row.files.length > 0 ? (
                      <button
                        onClick={() => setLightbox({ files: row.files, index: 0, code: row.code })}
                        className="block w-11 h-14 rounded overflow-hidden bg-zinc-800 border border-zinc-700 hover:ring-2 hover:ring-violet-500 transition-all"
                        title="Önizle"
                      >
                        <img
                          src={`${API_ORIGIN}/proxy/img/${row.files[0]!.drive_file_id}`}
                          alt={row.code}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ) : (
                      <div className="w-11 h-14 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-500 text-lg">🖼️</div>
                    )}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">
                    {fmtDate(row.job_created_at)}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-600">{row.code}</td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-700">{row.type}</td>
                  <td className="px-3 py-2 border-b border-zinc-100 max-w-[180px] truncate text-zinc-700" title={row.angle}>
                    {row.angle || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      row.stage === "Unaware" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                    }`}>{row.stage || "—"}</span>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      row.theme === "dark" ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-600"
                    }`}>{row.theme || "—"}</span>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-500">
                    {row.feature_name || "—"}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 text-center">
                    <button
                      onClick={() => setInspectRow({
                        code: row.code, type: row.type, angle: row.angle,
                        stage: row.stage, theme: row.theme, hook: row.hook,
                        body: row.body, cta: row.cta, image_prompt: row.image_prompt,
                      })}
                      className="text-zinc-400 hover:text-violet-600 transition-colors text-base leading-none"
                      title="Hook / Body / CTA / Prompt"
                    >
                      🔍
                    </button>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100">
                    <DriveFolderLink folderId={row.drive_folder} fileCount={row.files.length} />
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100">
                    <div className="flex gap-1.5 flex-wrap">
                      <button
                        onClick={() => navigate(
                          `/products/${productId}/creation/avatar`,
                          { state: makeAvatarState(row) },
                        )}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 whitespace-nowrap transition-colors"
                        title="Bu kreatifte Avatar video üret"
                      >
                        🎬 Avatar
                      </button>
                      <button
                        onClick={() => navigate(
                          `/products/${productId}/edit/image-to-video`,
                          { state: { defaultCodes: [row.code], defaultOperation: "version" } },
                        )}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 whitespace-nowrap transition-colors"
                        title="Bu kreatifte versiyon üret"
                      >
                        🔀 Versiyonla
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inspectRow && <PromptModal row={inspectRow} onClose={() => setInspectRow(null)} />}
      {lightbox && <ImageLightbox {...lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ─── IMAGE LIGHTBOX ───────────────────────────────────────────────────────────
function ImageLightbox({ files, index, code, onClose }: { files: MediaFile[]; index: number; code: string; onClose: () => void }) {
  const [idx, setIdx] = useState(index);
  const file = files[idx];
  if (!file) return null;
  const src = `${API_ORIGIN}/proxy/img/${file.drive_file_id}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-lg w-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-8 right-0 text-white text-sm hover:text-zinc-300">✕ Kapat</button>
        <div className="text-white text-xs font-mono">{code} · {file.language} · {file.size}</div>
        <img src={src} alt={file.file_name} className="max-h-[75vh] w-auto rounded-xl shadow-2xl" />
        {files.length > 1 && (
          <div className="flex gap-2 flex-wrap justify-center max-w-full">
            {files.map((f, i) => (
              <button
                key={f.id}
                onClick={() => setIdx(i)}
                className={`w-10 h-12 rounded overflow-hidden border-2 transition-all ${i === idx ? "border-violet-500" : "border-transparent opacity-60 hover:opacity-100"}`}
                title={`${f.language} · ${f.size}`}
              >
                <img src={`${API_ORIGIN}/proxy/img/${f.drive_file_id}`} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <a
          href={`${API_ORIGIN}/proxy/img/${file.drive_file_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-violet-300 hover:text-violet-200"
        >
          Tam boyut aç ↗
        </a>
      </div>
    </div>
  );
}

// ─── VIDEO PLAYER POPUP ───────────────────────────────────────────────────────

function VideoPlayerPopup({ fileId, label }: { fileId: string; label: string }) {
  const [open, setOpen] = useState(false);
  const src = `${API_ORIGIN}/proxy/media/${fileId}?ct=video%2Fmp4`;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 whitespace-nowrap"
      >
        ▶ {label}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(false)}
              className="absolute -top-8 right-0 text-white text-sm hover:text-zinc-300"
            >
              ✕ Kapat
            </button>
            <video
              src={src}
              controls
              autoPlay
              className="w-full rounded-xl shadow-2xl"
              style={{ maxHeight: "80vh" }}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ─── SCENARIO TAB ─────────────────────────────────────────────────────────────

function ScenarioTab({ productId }: { productId: string }) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string>("Tarih");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const stopRef = useRef(false);

  function handleSort(col: string) {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("asc"); }
  }

  function getVal(j: any, col: string): string {
    const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
    const state  = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
    switch (col) {
      case "Tarih":      return j.created_at ?? "";
      case "Tür":        return params.creative_type ?? "";
      case "Pain Point": return params.pain_point ?? "";
      case "Audience":   return params.audience ?? "";
      case "App":        return params.app ?? "";
      case "Kod":        return state.code ?? "";
      case "Step":       return j.step ?? "";
      case "Status":     return j.status ?? "";
      case "Maliyet":    return String(Number(j.cost_usd ?? 0));
      default:           return "";
    }
  }

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const [ugcRes, sbRes] = await Promise.all([
        api.listUGCJobs(productId).catch(() => ({ ugc_jobs: [] })),
        api.listStoryboardJobs(productId).catch(() => ({ storyboard_jobs: [] })),
      ]);
      if (!stopRef.current) {
        const ugcJobs = (ugcRes.ugc_jobs ?? []);
        const sbJobs = (sbRes.storyboard_jobs ?? []).map((j: any) => {
          const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
          return { ...j, _is_storyboard: true, params_json: JSON.stringify({ ...params, creative_type: "Storyboard" }) };
        });
        setJobs([...ugcJobs, ...sbJobs].sort((a: any, b: any) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function cancel(jobId: string, isStoryboard?: boolean) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      if (isStoryboard) await api.cancelStoryboardJob(jobId);
      else await api.cancelUGCJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (jobs.length === 0) return <div className="p-6 text-sm text-zinc-500">Henüz Scenario job yok.</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Tarih", "Tür", "Pain Point", "Audience", "App", "Kod", "Step", "Status", "Maliyet", "Link", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">
                {h && h !== "Link" ? (
                  <button onClick={() => handleSort(h)} className="flex items-center gap-1 hover:text-zinc-900">
                    {h}
                    <span className="text-zinc-300">
                      {sortCol === h ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                ) : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...jobs].sort((a, b) => {
            const av = getVal(a, sortCol);
            const bv = getVal(b, sortCol);
            const cmp = av.localeCompare(bv, undefined, { numeric: true });
            return sortDir === "asc" ? cmp : -cmp;
          }).map((j, i) => {
            const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
            const state  = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
            return (
              <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                    {params.creative_type ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 max-w-[140px] truncate" title={params.pain_point ?? ""}>{params.pain_point ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 max-w-[140px] truncate" title={params.audience ?? ""}>{params.audience ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 whitespace-nowrap">{params.app ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500 whitespace-nowrap">
                  {state.code ? (
                    <span className="font-mono text-xs font-semibold text-zinc-700">{state.code}</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500">{j.step ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-600">${Number(j.cost_usd ?? 0).toFixed(4)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  {(() => {
                    const driveFileId = state.finalDriveFileId
                      ?? state.finalVideoUrl?.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1]
                      ?? null;
                    const videoUrl = state.finalVideoUrl ?? (j._is_storyboard ? j.output_url : null);
                    return (
                      <div className="flex items-center gap-2">
                        {driveFileId ? (
                          <VideoPlayerPopup fileId={driveFileId} label={state.code ?? "▶"} />
                        ) : null}
                        {videoUrl ? (
                          <a href={videoUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Drive ↗</a>
                        ) : <span className="text-zinc-300">—</span>}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <div className="flex gap-2 items-center">
                    {j.status === "queued" && !j._is_storyboard && (
                      <button
                        onClick={async () => {
                          await api.retryUGCJob(j.id);
                          setJobs((prev) => prev.map((x: any) => x.id === j.id ? { ...x, status: "queued", step: "pending" } : x));
                        }}
                        className="text-xs text-blue-600 hover:underline">
                        tekrar tetikle
                      </button>
                    )}
                    {(j.status === "queued" || j.status === "running") && (
                      <button disabled={cancelling === j.id} onClick={() => cancel(j.id, j._is_storyboard)}
                        className="text-xs text-red-500 hover:underline disabled:opacity-40">
                        {cancelling === j.id ? "…" : "durdur"}
                      </button>
                    )}
                    {(j.status === "failed" || j.status === "cancelled") && (
                      <button
                        onClick={async () => {
                          try {
                            if (j._is_storyboard) await api.retryStoryboardJob(j.id);
                            else await api.retryUGCJob(j.id);
                            setJobs((prev) => prev.map((x: any) => x.id === j.id ? { ...x, status: "queued", step: "pending" } : x));
                          } catch (e) { alert("Retry: " + (e as Error).message); }
                        }}
                        className="text-xs text-amber-600 hover:underline"
                      >
                        tekrar dene
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-xs text-zinc-400">{jobs.length} kayıt</div>
    </div>
  );
}

// ─── AVATAR TAB ───────────────────────────────────────────────────────────────

function AvatarTab({ productId }: { productId: string }) {
  const navigate   = useNavigate();
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [converting, setConverting] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const r = await api.listAvatarJobs(productId).catch(() => ({ avatar_jobs: [] }));
      if (!stopRef.current) {
        setJobs([...(r.avatar_jobs ?? [])].sort((a: any, b: any) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function convertToHookBody(code: string) {
    setConverting(code);
    try {
      const r = await api.createEditJob({ product_id: productId, codes: [code], operation: "hook_body_compose" });
      alert(`Hook/Body seçimi başlatıldı (edit job: ${r.id}). Sonucu birkaç saniye sonra bu koda ait edit job kaydında görebilirsin — FFmpeg birleştirme henüz eklenmedi, sadece asset seçimi yapılıyor.`);
    } catch (e) { alert("Başlatılamadı: " + (e as Error).message); }
    finally { setConverting(null); }
  }

  async function cancel(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      await api.cancelAvatarJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (jobs.length === 0) return <div className="p-6 text-sm text-zinc-500">Henüz Avatar job yok.</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Tarih", "Aktör", "Audience", "Pain Point", "Metin (özet)", "Kod", "Status", "Maliyet", "Link", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j, i) => {
            const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
            return (
              <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700 whitespace-nowrap">
                    {params.actorName ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-600">{params.audience ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-600">{params.pain_point ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 max-w-[220px] truncate text-zinc-600" title={params.text}>
                  {params.text ? params.text.slice(0, 80) + (params.text.length > 80 ? "…" : "") : "—"}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500 whitespace-nowrap">
                  {j.code ? (
                    <span className="font-mono text-xs font-semibold text-zinc-700">{j.code}</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-600">${Number(j.cost_usd ?? 0).toFixed(4)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  {j.output_url ? (
                    <a href={j.output_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Drive ↗</a>
                  ) : j.status === "failed" && j.error ? (
                    <span className="text-xs text-red-500 truncate max-w-[120px] block" title={j.error}>{j.error.slice(0, 40)}…</span>
                  ) : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <div className="flex items-center gap-2">
                    {j.status === "completed" && (
                      <button
                        onClick={() => {
                          const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
                          const imageCodeParam = params.image_code ? `&image_code=${params.image_code}` : "";
                          navigate(`/products/${productId}/creation/compose?avatar_job_id=${j.id}${imageCodeParam}`);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 whitespace-nowrap transition-colors"
                        title="Bu avatar ile görsel birleştir"
                      >
                        🎬 Birleştir
                      </button>
                    )}
                    {j.status === "completed" && j.code && (
                      <button
                        disabled={converting === j.code}
                        onClick={() => convertToHookBody(j.code)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 hover:bg-violet-100 whitespace-nowrap transition-colors disabled:opacity-40"
                        title="Hook/Body havuzundan feature'a uygun asset seç (FFmpeg birleştirme henüz eklenmedi)"
                      >
                        🪝 {converting === j.code ? "…" : "Hook Body Avatar"}
                      </button>
                    )}
                    {(j.status === "queued" || j.status === "running") && (
                      <button disabled={cancelling === j.id} onClick={() => cancel(j.id)}
                        className="text-xs text-red-500 hover:underline disabled:opacity-40">
                        {cancelling === j.id ? "…" : "durdur"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-xs text-zinc-400">{jobs.length} kayıt</div>
    </div>
  );
}

// ─── COMPOSE TAB ──────────────────────────────────────────────────────────────

function ComposeTab({ productId }: { productId: string }) {
  const navigate  = useNavigate();
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const r = await api.listComposeJobs(productId).catch(() => ({ compose_jobs: [] }));
      if (!stopRef.current) {
        setJobs([...(r.compose_jobs ?? [])].sort((a: any, b: any) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function cancel(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      await api.cancelComposeJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  const FORMAT_LABEL: Record<string, string> = {
    split: "Split",
    takeover: "Takeover",
    rembg: "Rembg Overlay",
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 gap-3">
      <div className="flex justify-end shrink-0">
        <button
          onClick={() => navigate(`/products/${productId}/creation/compose`)}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition"
        >
          + Yeni Birleştirme
        </button>
      </div>
      {jobs.length === 0 ? (
        <div className="text-sm text-zinc-500">Henüz compose job yok.</div>
      ) : (
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                {["Tarih", "Format", "Kod", "Status", "Link", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j, i) => (
                <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                  <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                  <td className="px-3 py-2 border-b border-zinc-100">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                      {FORMAT_LABEL[j.format] ?? j.format}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-600">{j.image_code}</td>
                  <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                  <td className="px-3 py-2 border-b border-zinc-100">
                    {j.output_url ? (
                      <a href={j.output_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Drive ↗</a>
                    ) : j.error ? (
                      <span className="text-xs text-red-500 truncate max-w-[120px] block" title={j.error}>{j.error.slice(0,40)}…</span>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2 border-b border-zinc-100">
                    <div className="flex gap-2">
                      {(j.status === "failed" || j.status === "cancelled") && (
                        <button
                          onClick={async () => {
                            try {
                              await api.retryComposeJob(j.id);
                              setJobs((prev) => prev.map((x: any) => x.id === j.id ? { ...x, status: "queued", error: null } : x));
                            } catch (e) { alert("Retry: " + (e as Error).message); }
                          }}
                          className="text-xs text-amber-600 hover:underline"
                        >tekrar dene</button>
                      )}
                      {(j.status === "queued" || j.status === "running" || j.status === "waiting") && (
                        <button disabled={cancelling === j.id} onClick={() => cancel(j.id)}
                          className="text-xs text-red-500 hover:underline disabled:opacity-40">
                          {cancelling === j.id ? "…" : "durdur"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 px-4 pb-2 text-xs text-zinc-400">{jobs.length} kayıt</div>
        </div>
      )}
    </div>
  );
}

// ─── LONG STORY / VOX AD TABS ─────────────────────────────────────────────────
// Long Story: her shot kendi step'i biter bitmez Drive'a yüklenip state_json'a
// (shot_drive_urls) yazılıyor (bkz. long-story-runner.ts) — burada shot bazlı
// oynatılabilir grid olarak gösteriliyor, job bitmesini beklemeye gerek yok.
// Vox Ad: concat sonunda tek bir final video üretiyor (state.output_url) — job
// tamamlanınca burada doğrudan oynatılabiliyor.

interface AsyncPipelineJob {
  id: string;
  feature_name: string;
  audience: string;
  pain_point: string;
  step: string;
  status: string;
  code: string | null;
  error: string | null;
  state_json?: string | null;
  created_at: string;
  finished_at: string | null;
}

function driveFileIdFromUrl(url?: string | null): string | null {
  return url?.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] ?? null;
}

function AsyncPipelineTab({
  productId, fetchFn, cancelFn, title, emptyText, kind,
}: {
  productId: string;
  fetchFn: (productId: string) => Promise<{ jobs: AsyncPipelineJob[] }>;
  cancelFn: (id: string) => Promise<{ ok: boolean }>;
  title: string;
  emptyText: string;
  kind: "long_story" | "vox_ad" | "broll_ad";
}) {
  const [jobs, setJobs] = useState<AsyncPipelineJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const r = await fetchFn(productId).catch(() => ({ jobs: [] }));
      if (!stopRef.current) {
        setJobs([...r.jobs].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 8000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId, fetchFn]);

  async function cancel(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi? O ana kadar Drive'a yüklenmiş shot/video'lar kalır, sadece kalan adımlar durur.")) return;
    setCancelling(jobId);
    try {
      await cancelFn(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (jobs.length === 0) return <div className="p-6 text-sm text-zinc-500">{emptyText}</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      <div className="mb-2 text-xs text-zinc-400">{title} — {jobs.length} kayıt</div>
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Tarih", "Feature", "Audience", "Pain Point", "İlerleme", "Durum", "Kod", "Video", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.flatMap((j, i) => {
            const state = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
            const rowBg = i % 2 === 0 ? "bg-white" : "bg-zinc-50/60";
            const isExpanded = !!expanded[j.id];
            const shotUrls: (string | null)[] = Array.isArray(state.shot_drive_urls) ? state.shot_drive_urls : [];
            const hasShots = shotUrls.some(Boolean);

            const mainRow = (
              <tr key={j.id} className={rowBg}>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap">{j.feature_name || "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 max-w-[160px] truncate" title={j.audience}>{j.audience || "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 max-w-[200px] truncate" title={j.pain_point}>{j.pain_point || "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500 whitespace-nowrap">
                  {kind === "long_story" && Array.isArray(state.shots)
                    ? `${shotUrls.filter(Boolean).length}/${state.shots.length} shot${(state.skipped_shots?.length ? ` (${state.skipped_shots.length} atlandı)` : "")}`
                    : kind === "broll_ad" && Array.isArray(state.clips)
                    ? (j.status === "completed" ? "done" : `${(state.clip_drive_urls ?? []).filter(Boolean).length}/${state.clips.length} clip · ${j.step ?? "—"}`)
                    : (j.status === "completed" ? "done" : (j.step ?? "—"))}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-700 whitespace-nowrap">
                  {j.code
                    ? <span title="Drive'da ürün klasörü → feature adı → bu kodun klasöründe">{j.code}</span>
                    : j.status === "failed" && j.error
                      ? <span className="text-red-500 truncate max-w-[140px] block" title={j.error}>{j.error.slice(0, 40)}…</span>
                      : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  {kind === "vox_ad" || (kind === "broll_ad" && state.output_url) ? (() => {
                    const fid = driveFileIdFromUrl(state.output_url);
                    return fid ? (
                      <div className="flex items-center gap-2">
                        <VideoPlayerPopup fileId={fid} label="İzle" />
                        <a href={state.output_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Drive ↗</a>
                      </div>
                    ) : <span className="text-zinc-300">—</span>;
                  })() : (kind === "long_story" && hasShots) || (kind === "broll_ad" && (state.talking_video_drive_url || (state.clip_drive_urls ?? []).some(Boolean))) ? (
                    <button
                      onClick={() => setExpanded((prev) => ({ ...prev, [j.id]: !prev[j.id] }))}
                      className="text-xs text-indigo-600 hover:underline whitespace-nowrap"
                    >
                      {isExpanded ? "▾ parçaları gizle" : "▸ parçaları göster"}
                    </button>
                  ) : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  {(j.status === "queued" || j.status === "running") && (
                    <button disabled={cancelling === j.id} onClick={() => cancel(j.id)}
                      className="text-xs text-red-500 hover:underline disabled:opacity-40 whitespace-nowrap">
                      {cancelling === j.id ? "…" : "durdur"}
                    </button>
                  )}
                </td>
              </tr>
            );

            if (kind === "vox_ad" || !isExpanded) return [mainRow];

            const expandRow = kind === "long_story" ? (
              <tr key={`${j.id}-shots`} className={rowBg}>
                <td colSpan={9} className="px-3 py-3 border-b border-zinc-100">
                  <div className="flex flex-wrap gap-2">
                    {(state.shots ?? []).map((_: any, shotIdx: number) => {
                      const url = shotUrls[shotIdx];
                      const fid = driveFileIdFromUrl(url);
                      const skipped = (state.skipped_shots ?? []).includes(shotIdx);
                      const label = `Shot ${String(shotIdx + 1).padStart(2, "0")}`;
                      return fid ? (
                        <VideoPlayerPopup key={shotIdx} fileId={fid} label={label} />
                      ) : (
                        <span key={shotIdx} className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${skipped ? "bg-red-50 text-red-400" : "bg-zinc-100 text-zinc-400"}`}>
                          {label}{skipped ? " (atlandı)" : "…"}
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ) : (
              // broll_ad: aktör videosu + her B-roll clip'i + (varsa) birleştirilmiş B-roll
              <tr key={`${j.id}-parts`} className={rowBg}>
                <td colSpan={9} className="px-3 py-3 border-b border-zinc-100">
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const actorFid = driveFileIdFromUrl(state.talking_video_drive_url);
                      return actorFid ? <VideoPlayerPopup key="actor" fileId={actorFid} label="Aktör" /> : null;
                    })()}
                    {((state.clips ?? []) as any[]).map((_: any, clipIdx: number) => {
                      const url = (state.clip_drive_urls ?? [])[clipIdx];
                      const fid = driveFileIdFromUrl(url);
                      const skipped = (state.skipped_clips ?? []).includes(clipIdx);
                      const label = `B-Roll ${String(clipIdx + 1).padStart(2, "0")}`;
                      return fid ? (
                        <VideoPlayerPopup key={clipIdx} fileId={fid} label={label} />
                      ) : (
                        <span key={clipIdx} className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${skipped ? "bg-red-50 text-red-400" : "bg-zinc-100 text-zinc-400"}`}>
                          {label}{skipped ? " (atlandı)" : "…"}
                        </span>
                      );
                    })}
                    {(() => {
                      const stitchedFid = driveFileIdFromUrl(state.broll_stitched_drive_url);
                      return stitchedFid ? <VideoPlayerPopup key="stitched" fileId={stitchedFid} label="B-Roll (birleşik)" /> : null;
                    })()}
                  </div>
                </td>
              </tr>
            );
            return [mainRow, expandRow];
          })}
        </tbody>
      </table>
    </div>
  );
}

function LongStoryTab({ productId }: { productId: string }) {
  return (
    <AsyncPipelineTab
      productId={productId}
      kind="long_story"
      fetchFn={async (pid) => { const r = await api.listLongStoryJobs(pid); return { jobs: r.long_story_jobs as any }; }}
      cancelFn={api.cancelLongStoryJob}
      title="Long Story"
      emptyText="Henüz Long Story üretilmedi. Create'ten 'long story üret' diyerek başlatabilirsin."
    />
  );
}

function VoxAdTab({ productId }: { productId: string }) {
  return (
    <AsyncPipelineTab
      productId={productId}
      kind="vox_ad"
      fetchFn={async (pid) => { const r = await api.listVoxAdJobs(pid); return { jobs: r.vox_ad_jobs as any }; }}
      cancelFn={api.cancelVoxAdJob}
      title="Vox Ad"
      emptyText="Henüz Vox Ad üretilmedi. Create'ten 'vox ad üret' diyerek başlatabilirsin."
    />
  );
}

function BrollAdTab({ productId }: { productId: string }) {
  return (
    <AsyncPipelineTab
      productId={productId}
      kind="broll_ad"
      fetchFn={async (pid) => { const r = await api.listBrollAdJobs(pid); return { jobs: r.broll_ad_jobs as any }; }}
      cancelFn={api.cancelBrollAdJob}
      title="B-Roll Ad"
      emptyText="Henüz B-Roll Ad üretilmedi. Create'ten 'b-roll ad üret' diyerek başlatabilirsin."
    />
  );
}

// ─── ALL TAB ──────────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { label: string; cls: string }> = {
  image:    { label: "Image",    cls: "bg-blue-100 text-blue-700" },
  scenario: { label: "Scenario", cls: "bg-amber-100 text-amber-700" },
  avatar:   { label: "Avatar",   cls: "bg-teal-100 text-teal-700" },
  long_story: { label: "Long Story", cls: "bg-pink-100 text-pink-700" },
  vox_ad:     { label: "Vox Ad",     cls: "bg-orange-100 text-orange-700" },
  broll_ad:   { label: "B-Roll Ad",  cls: "bg-emerald-100 text-emerald-700" },
  edit:     { label: "Edit",     cls: "bg-violet-100 text-violet-700" },
  compose:  { label: "Compose",  cls: "bg-indigo-100 text-indigo-700" },
};

function TypeBadge({ type }: { type: string }) {
  const meta = TYPE_META[type] ?? { label: type, cls: "bg-zinc-100 text-zinc-600" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

interface UnifiedRow {
  id: string;
  type: string;
  created_at: string;
  status: string;
  identifier: string;
  detail: string;
  link?: string;
}

function AllTab({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<UnifiedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const [jobs, ugc, avatar, edit, compose, longStory, voxAd, brollAd] = await Promise.all([
        api.listJobs(productId).catch(() => ({ jobs: [] })),
        api.listUGCJobs(productId).catch(() => ({ ugc_jobs: [] })),
        api.listAvatarJobs(productId).catch(() => ({ avatar_jobs: [] })),
        api.listEditJobs(productId).catch(() => ({ edit_jobs: [] })),
        api.listComposeJobs(productId).catch(() => ({ compose_jobs: [] })),
        api.listLongStoryJobs(productId).catch(() => ({ long_story_jobs: [] })),
        api.listVoxAdJobs(productId).catch(() => ({ vox_ad_jobs: [] })),
        api.listBrollAdJobs(productId).catch(() => ({ broll_ad_jobs: [] })),
      ]);
      if (stopRef.current) return;

      const unified: UnifiedRow[] = [];

      for (const j of (jobs.jobs ?? [])) {
        const p = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
        unified.push({
          id: j.id, type: "image", created_at: j.created_at ?? "",
          status: j.status,
          identifier: p.angle_names?.[0] ?? p.kind ?? "image",
          detail: `${p.types?.[0] ?? "—"} · ${p.languages?.[0] ?? ""}`,
        });
      }
      for (const j of (ugc.ugc_jobs ?? [])) {
        const p = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
        unified.push({
          id: j.id, type: "scenario", created_at: j.created_at ?? "",
          status: j.status,
          identifier: p.creative_type ?? "UGC",
          detail: p.pain_point ?? "—",
        });
      }
      for (const j of (avatar.avatar_jobs ?? [])) {
        const p = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
        unified.push({
          id: j.id, type: "avatar", created_at: j.created_at ?? "",
          status: j.status,
          identifier: p.actorName ?? "Aktör",
          detail: p.text ? p.text.slice(0, 60) + (p.text.length > 60 ? "…" : "") : "—",
          link: j.output_url ?? undefined,
        });
      }
      for (const j of (edit.edit_jobs ?? [])) {
        const codes: string[] = (() => { try { return JSON.parse(j.codes_json ?? "[]"); } catch { return []; } })();
        unified.push({
          id: j.id, type: "edit", created_at: j.created_at ?? "",
          status: j.status,
          identifier: OP_LABEL[j.operation] ?? j.operation,
          detail: codes.slice(0, 3).join(", ") + (codes.length > 3 ? ` +${codes.length - 3}` : ""),
        });
      }
      for (const j of (compose.compose_jobs ?? [])) {
        unified.push({
          id: j.id, type: "compose", created_at: j.created_at ?? "",
          status: j.status,
          identifier: j.format ?? "—",
          detail: j.image_code ?? "—",
          link: j.output_url ?? undefined,
        });
      }
      for (const j of (longStory.long_story_jobs ?? [])) {
        const s = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
        const shotCount = Array.isArray(s.shots) ? s.shots.length : 0;
        const doneCount = Array.isArray(s.shot_drive_urls) ? s.shot_drive_urls.filter(Boolean).length : 0;
        unified.push({
          id: j.id, type: "long_story", created_at: j.created_at ?? "",
          status: j.status,
          identifier: j.feature_name ?? "Long Story",
          detail: j.code ? `Kod: ${j.code} · ${doneCount}/${shotCount} shot` : (j.error ?? "—"),
          link: s.code_folder_id ? `https://drive.google.com/drive/folders/${s.code_folder_id}` : undefined,
        });
      }
      for (const j of (voxAd.vox_ad_jobs ?? [])) {
        const s = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
        unified.push({
          id: j.id, type: "vox_ad", created_at: j.created_at ?? "",
          status: j.status,
          identifier: j.feature_name ?? "Vox Ad",
          detail: j.code ? `Kod: ${j.code}` : (j.error ?? "—"),
          link: s.output_url ?? undefined,
        });
      }
      for (const j of (brollAd.broll_ad_jobs ?? [])) {
        const s = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
        const clipCount = Array.isArray(s.clips) ? s.clips.length : 0;
        const doneCount = Array.isArray(s.clip_drive_urls) ? s.clip_drive_urls.filter(Boolean).length : 0;
        unified.push({
          id: j.id, type: "broll_ad", created_at: j.created_at ?? "",
          status: j.status,
          identifier: j.feature_name ?? "B-Roll Ad",
          detail: j.code ? `Kod: ${j.code} · ${doneCount}/${clipCount} clip` : (j.error ?? "—"),
          link: s.output_url ?? undefined,
        });
      }

      unified.sort((a, b) => b.created_at.localeCompare(a.created_at));

      setRows(unified);
      setLoading(false);
    }
    tick();
    const id = setInterval(tick, 6000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (rows.length === 0) return <div className="p-6 text-sm text-zinc-500">Henüz üretim yok.</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      <div className="mb-2 text-xs text-zinc-400">{rows.length} kayıt — en yeni üstte</div>
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Tarih", "Tür", "Tanım", "Detay", "Durum", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
              <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-2 border-b border-zinc-100"><TypeBadge type={r.type} /></td>
              <td className="px-3 py-2 border-b border-zinc-100 font-medium text-zinc-700 whitespace-nowrap">{r.identifier}</td>
              <td className="px-3 py-2 border-b border-zinc-100 max-w-[200px] truncate text-zinc-500" title={r.detail}>{r.detail || "—"}</td>
              <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={r.status} /></td>
              <td className="px-3 py-2 border-b border-zinc-100">
                {r.link ? (
                  <a href={r.link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Drive ↗</a>
                ) : r.type === "avatar" && r.status === "completed" ? (
                  <button
                    onClick={() => {
                      const url = `/products/${productId}/creation/compose?avatar_job_id=${r.id}`;
                      navigate(url);
                    }}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Birleştir
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── EDIT TAB ─────────────────────────────────────────────────────────────────

function EditTab({ productId }: { productId: string }) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const r = await api.listEditJobs(productId).catch(() => ({ edit_jobs: [] }));
      if (!stopRef.current) {
        setJobs([...(r.edit_jobs ?? [])].sort((a: any, b: any) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 4000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function cancel(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      await api.cancelEditJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (jobs.length === 0) return <div className="p-6 text-sm text-zinc-500">Henüz Edit job yok.</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      {previewFileId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setPreviewFileId(null)}>
          <div className="relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreviewFileId(null)} className="absolute -top-8 right-0 text-white text-lg font-bold leading-none">✕</button>
            <video
              src={`${API_ORIGIN}/proxy/media/${previewFileId}?ct=video%2Fmp4`}
              controls autoPlay
              className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Tarih", "İşlem", "Kodlar", "Status", "Maliyet", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j, i) => {
            const codes: string[] = (() => { try { return JSON.parse(j.codes_json ?? "[]"); } catch { return []; } })();
            return (
              <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 whitespace-nowrap">
                    {OP_LABEL[j.operation] ?? j.operation}
                  </span>
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500">
                  {codes.slice(0, 4).join(", ")}{codes.length > 4 ? ` +${codes.length - 4}` : ""}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-600">${Number(j.cost_usd ?? 0).toFixed(4)}</td>
                <td className="px-3 py-2 border-b border-zinc-100 flex gap-2 items-center">
                  {(j.status === "queued" || j.status === "running") && (
                    <button disabled={cancelling === j.id} onClick={() => cancel(j.id)}
                      className="text-xs text-red-500 hover:underline disabled:opacity-40">
                      {cancelling === j.id ? "…" : "durdur"}
                    </button>
                  )}
                  {j.status === "completed" && (() => {
                    try {
                      const result = JSON.parse(j.result_json ?? "null");
                      if (result?.croppedFileId) {
                        return (
                          <button onClick={() => setPreviewFileId(result.croppedFileId)}
                            className="text-xs text-blue-500 hover:underline">
                            ▶ önizle
                          </button>
                        );
                      }
                    } catch {}
                    return null;
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-xs text-zinc-400">{jobs.length} kayıt</div>
    </div>
  );
}

// ─── KAMPANYA TAB ─────────────────────────────────────────────────────────────

function KodlarModal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const [data, setData] = useState<{ image_codes: any[]; ugc_jobs: any[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCampaignCodes(campaignId)
      .then(r => setData(r))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  const statusColor = (s: string) =>
    s === "completed" ? "text-green-600" : s === "failed" ? "text-red-500" : "text-amber-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="text-sm font-semibold text-zinc-800">Kodlar</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 text-lg leading-none">✕</button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-400 text-sm">Yükleniyor…</div>
        ) : (
          <div className="flex divide-x divide-zinc-100 overflow-hidden flex-1 min-h-0">
            {/* Left: image codes */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
                Görsel Kodları ({data?.image_codes.length ?? 0})
              </div>
              {(data?.image_codes.length ?? 0) === 0 ? (
                <p className="text-xs text-zinc-400">Kod bulunamadı.</p>
              ) : (
                <div className="space-y-0.5">
                  {data!.image_codes.map(c => (
                    <div key={c.code} className="font-mono text-sm text-zinc-800 py-0.5">
                      BIQ{c.code}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Right: ugc jobs */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
                Video Kodları ({data?.ugc_jobs.length ?? 0})
              </div>
              {(data?.ugc_jobs.length ?? 0) === 0 ? (
                <p className="text-xs text-zinc-400">Video bulunamadı.</p>
              ) : (
                <div className="space-y-0.5">
                  {data!.ugc_jobs.map(j => (
                    <div key={j.id} className="font-mono text-sm text-zinc-800 py-0.5">
                      {j.id.slice(0, 8)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KampanyaTab({ productId }: { productId: string }) {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [kodlarOpen, setKodlarOpen] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.listCampaigns(productId)
      .then(r => setCampaigns(r.campaigns))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  async function handleCancel(id: string) {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.cancelCampaign(id);
      setCampaigns(cs => cs.map(c => c.id === id ? { ...c, _cancelled: true } : c));
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Bu bulk üretimi listeden kaldır?")) return;
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.deleteCampaign(id);
      setCampaigns(cs => cs.filter(c => c.id !== id));
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  }

  if (loading) return <div className="p-8 text-center text-zinc-400 text-sm">Yükleniyor…</div>;
  if (!campaigns.length) return <div className="p-8 text-center text-zinc-400 text-sm">Henüz bulk üretim yok.</div>;

  return (
    <div className="p-5 overflow-y-auto h-full">
      <div className="space-y-3">
        {campaigns.map((c) => {
          const totalJobs = c.ugc.total + c.images.total;
          const completedJobs = c.ugc.completed + c.images.completed;
          const failedJobs = c.ugc.failed + c.images.failed;
          const runningJobs = totalJobs - completedJobs - failedJobs;
          const pct = totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;
          const date = c.created_at
            ? new Date(c.created_at.replace(" ", "T") + "Z").toLocaleDateString("tr-TR", {
                day: "2-digit", month: "2-digit", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })
            : "";

          return (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-zinc-800 truncate">{c.pain_point || c.name}</span>
                </div>
                {c.audience && (
                  <div className="text-xs text-zinc-500 truncate mb-2">{c.audience}</div>
                )}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : "bg-violet-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0">{completedJobs}/{totalJobs}</span>
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap items-center">
                  {c.ugc.total > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                      {c.ugc.completed}/{c.ugc.total} UGC
                    </span>
                  )}
                  {c.images.total > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                      {c.images.completed}/{c.images.total} Image
                    </span>
                  )}
                  {runningJobs > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                      {runningJobs} devam ediyor
                    </span>
                  )}
                  {failedJobs > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                      {failedJobs} hata
                    </span>
                  )}
                  <button
                    onClick={() => setKodlarOpen(c.id)}
                    className="px-2.5 py-0.5 rounded-lg text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors"
                  >
                    Kodlar
                  </button>
                  {!c._cancelled && runningJobs > 0 && (
                    <button
                      onClick={() => handleCancel(c.id)}
                      disabled={busy[c.id]}
                      className="px-2.5 py-0.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200 disabled:opacity-50 transition-colors"
                    >
                      {busy[c.id] ? "…" : "Durdur"}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(c.id)}
                    disabled={busy[c.id]}
                    className="px-2.5 py-0.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition-colors"
                  >
                    {busy[c.id] ? "…" : "Sil"}
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <span className="text-xs text-zinc-400">{date}</span>
                {c.drive_url ? (
                  <a
                    href={c.drive_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                  >
                    Drive&apos;da Aç
                  </a>
                ) : (
                  <span className="text-xs text-zinc-400 italic">Klasör oluşturuluyor…</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {kodlarOpen && (
        <KodlarModal campaignId={kodlarOpen} onClose={() => setKodlarOpen(null)} />
      )}
    </div>
  );
}

// ─── STORYBOARD TAB ───────────────────────────────────────────────────────────

const CREATOR_TYPE_LABELS: Record<string, string> = {
  young_woman:  "Genç Kadın",
  young_man:    "Genç Adam",
  mature_woman: "Olgun Kadın",
  mature_man:   "Olgun Adam",
};

function StoryboardTab({ productId }: { productId: string }) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    async function tick() {
      const r = await api.listStoryboardJobs(productId).catch(() => ({ storyboard_jobs: [] }));
      if (!stopRef.current) {
        setJobs([...(r.storyboard_jobs ?? [])].sort((a: any, b: any) => (b.created_at ?? "").localeCompare(a.created_at ?? "")));
        setLoading(false);
      }
    }
    tick();
    const id = setInterval(tick, 5000);
    return () => { stopRef.current = true; clearInterval(id); };
  }, [productId]);

  async function cancel(jobId: string) {
    if (!confirm("Bu job iptal edilsin mi?")) return;
    setCancelling(jobId);
    try {
      await api.cancelStoryboardJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "cancelled" } : j));
    } catch (e) { alert("İptal edilemedi: " + (e as Error).message); }
    finally { setCancelling(null); }
  }

  async function retry(jobId: string) {
    try {
      await api.retryStoryboardJob(jobId);
      setJobs((prev) => prev.map((j) => j.id === jobId ? { ...j, status: "queued" } : j));
    } catch (e) { alert("Retry: " + (e as Error).message); }
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500">Yükleniyor…</div>;
  if (jobs.length === 0) return <div className="p-6 text-sm text-zinc-500">Henüz Storyboard job yok.</div>;

  return (
    <div className="p-4 overflow-auto h-full">
      <table className="w-full text-xs border-collapse border rounded-lg overflow-hidden">
        <thead className="bg-zinc-50 sticky top-0">
          <tr>
            {["Görsel", "Tarih", "Creator Type", "Feature", "Audience", "Pain Point", "Step", "Status", "Link", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-zinc-600 border-b border-zinc-200 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j, i) => {
            const params = (() => { try { return JSON.parse(j.params_json ?? "{}"); } catch { return {}; } })();
            const state = (() => { try { return JSON.parse(j.state_json ?? "{}"); } catch { return {}; } })();
            const creatorLabel = CREATOR_TYPE_LABELS[params.creator_type] ?? params.creator_type ?? "—";
            const heroImageUrl: string | null = j.output_image_url ?? state.hero_image_url ?? null;
            return (
              <tr key={j.id} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/60"}>
                <td className="px-3 py-2 border-b border-zinc-100">
                  {heroImageUrl ? (
                    <a href={heroImageUrl} target="_blank" rel="noreferrer">
                      <img src={heroImageUrl} alt="storyboard" className="w-10 h-16 object-cover rounded border border-zinc-200 hover:opacity-80 transition-opacity" />
                    </a>
                  ) : (
                    <div className="w-10 h-16 bg-zinc-100 rounded border border-zinc-200 flex items-center justify-center text-zinc-300 text-base">🎬</div>
                  )}
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 whitespace-nowrap text-zinc-400">{fmtDate(j.created_at)}</td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700 whitespace-nowrap">{creatorLabel}</span>
                </td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 max-w-[100px] truncate" title={params.feature_name ?? ""}>{params.feature_name ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 max-w-[120px] truncate" title={params.audience ?? ""}>{params.audience ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 text-zinc-600 max-w-[120px] truncate" title={params.pain_point ?? ""}>{params.pain_point ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100 font-mono text-zinc-500 whitespace-nowrap">{j.step ?? "—"}</td>
                <td className="px-3 py-2 border-b border-zinc-100"><StatusBadge status={j.status} /></td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <div className="flex flex-col gap-1">
                    {j.output_url && (
                      <a href={j.output_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">Video ↗</a>
                    )}
                    {j.error && !j.output_url && (
                      <span className="text-xs text-red-500 truncate max-w-[140px] block" title={j.error}>{j.error.slice(0, 50)}…</span>
                    )}
                    {!j.output_url && !j.error && <span className="text-zinc-300">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2 border-b border-zinc-100">
                  <div className="flex gap-2 items-center">
                    {(j.status === "queued" || j.status === "running") && (
                      <button disabled={cancelling === j.id} onClick={() => cancel(j.id)}
                        className="text-xs text-red-500 hover:underline disabled:opacity-40">
                        {cancelling === j.id ? "…" : "durdur"}
                      </button>
                    )}
                    {(j.status === "failed" || j.status === "cancelled") && (
                      <button onClick={() => retry(j.id)} className="text-xs text-amber-600 hover:underline">
                        tekrar dene
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 text-xs text-zinc-400">{jobs.length} kayıt</div>
    </div>
  );
}
